package dev.lingvo.site.components.review

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.ReviewQueueDto
import dev.lingvo.shared.SourceViewDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlin.math.ceil

/**
 * The queue a reviewer is actually working: which strings, in which order, and when to fetch more.
 *
 * Pure and separate from the page, because the cursor indexes *this* list — an off-by-one here
 * decides a different string from the one on screen.
 */

/** The job a reviewer has chosen out of the queue in front of them. */
internal enum class ReviewScope {
    /** Everything on this screen, in the order the server sent it. */
    ALL,

    /**
     * Approved once, changed since.
     *
     * **Re-review is a different job from first review.** A reviewer coming back after an import or
     * a source edit is usually looking for exactly these, and reading them mixed in with brand-new
     * work means holding two different questions at once.
     */
    REVISED,

    /**
     * Nothing QA would refuse.
     *
     * The scope the batch action works in, offered as a scope of its own so a reviewer can *read*
     * that set before deciding to approve it in one press.
     */
    QA_CLEAN,
}

/**
 * Whether this string is being reviewed again rather than for the first time.
 *
 * `previousApproved` is the last approved text, carried so the editor can draw a word diff. Its
 * presence is therefore the only honest signal available for "changed since approval": it is real
 * revision data rather than an inference from timestamps.
 */
internal fun changedSinceApproval(item: ReviewItemDto): Boolean = item.previousApproved != null

/** The queue a reviewer is actually working, given the scope they chose. */
internal fun reviewQueueScope(items: List<ReviewItemDto>, scope: ReviewScope): List<ReviewItemDto> =
    when (scope) {
        ReviewScope.ALL -> items
        ReviewScope.REVISED -> items.filter(::changedSinceApproval)
        ReviewScope.QA_CLEAN -> items.filter { !it.blocked }
    }

/**
 * The strings on this screen a batch approval may name.
 *
 * QA-clean is not enough on its own: a payload that cannot be shown faithfully — a value that is
 * scalar on one side and plural on the other — is refused by the single Approve button too, and a
 * batch must not be the way around a refusal the reviewer would have met one string at a time.
 */
internal fun reviewBatchEligible(items: List<ReviewItemDto>, guardedMode: Boolean = false): List<ReviewItemDto> =
    items.filter { !it.blocked && reviewPayloadIsRepresentable(it) && reviewSourceClaimAvailable(it, guardedMode = guardedMode) }

/** A UUID claim is stable across reads; legacy services use the timestamp they actually accepted. */
internal data class ReviewItemClaim(
    val keyId: String,
    val target: String,
    val source: SourceViewDto?,
)

internal fun ReviewItemDto.reviewClaim() = ReviewItemClaim(keyId, targetRevision ?: updatedAt.orEmpty(), sourceSnapshot?.view)

/** Counts describe one read, not the number decided during the whole sitting. */
internal data class ReviewReadBasis(val acknowledged: Int, val recovery: Int, val order: Int, val reloadToken: Int)
internal data class ReviewQueueRead(val queue: ReviewQueueDto, val basis: ReviewReadBasis)

private data class ReviewAcknowledgement(
    val submitted: ReviewItemClaim,
    val displayed: ReviewItemClaim,
    val qaClean: Boolean,
)

/**
 * Only exact, positively acknowledged claims leave the pending set. A new target or source claim
 * for the same key is new work. The read baseline prevents a reload subtracting earlier decisions
 * from counts which the server has already reduced.
 */
internal class ReviewFeedback {
    private var acknowledgements by mutableStateOf(emptyList<ReviewAcknowledgement>())
    private var recovery by mutableStateOf(0)
    private var readOrder = 0

    val decided: Int get() = acknowledgements.size

    fun captureRead(reloadToken: Int) = ReviewReadBasis(decided, recovery, ++readOrder, reloadToken)

    fun acknowledge(submitted: ReviewItemDto, displayed: ReviewItemDto = submitted) {
        val claim = submitted.reviewClaim()
        if (acknowledgements.any { it.submitted == claim }) return
        acknowledgements = acknowledgements + ReviewAcknowledgement(claim, displayed.reviewClaim(), !displayed.blocked)
    }

    fun acknowledged(item: ReviewItemDto): Boolean {
        val claim = item.reviewClaim()
        return acknowledgements.any { it.submitted == claim || it.displayed == claim }
    }

    fun pending(items: List<ReviewItemDto>) = items.asReversed().distinctBy { it.keyId }.asReversed().filterNot(::acknowledged)

    fun requireCurrentRead() { recovery++ }

    fun hasCurrentRead(read: ReviewQueueRead): Boolean = read.basis.recovery == recovery

    fun readStayedStable(basis: ReviewReadBasis): Boolean = basis.acknowledged == decided && basis.recovery == recovery

    /** A response cannot certify its SQL snapshot order from the browser's admission order. */
    suspend fun readQueue(slug: String, locale: String, reloadToken: Int): ReviewQueueRead {
        val basis = captureRead(reloadToken)
        val queue = ApiClient.reviewQueue(slug, locale)
        if (readStayedStable(basis)) return ReviewQueueRead(queue, basis)
        requireCurrentRead()
        return readCurrentCounts(slug, locale, reloadToken)
    }

    /** One post-acknowledgement read; another overlap is explicit recovery, never a read loop. */
    suspend fun readCurrentCounts(slug: String, locale: String, reloadToken: Int): ReviewQueueRead {
        val basis = captureRead(reloadToken)
        val queue = ApiClient.reviewQueue(slug, locale)
        check(readStayedStable(basis)) {
            tr("review.review_queue_model.queue_changed_during_read", "The queue changed while it was loading. Reload to confirm the current counts.")
        }
        return ReviewQueueRead(queue, basis)
    }

    private fun debits(read: ReviewQueueRead) = acknowledgements.drop(read.basis.acknowledged).filter { acknowledgement ->
        // A read that overlapped the decision can already contain a renewed submission. That is
        // current pending work, never a debit for the earlier claim of the same key.
        val current = read.queue.items.firstOrNull { it.keyId == acknowledgement.submitted.keyId }?.reviewClaim()
        current == null || current == acknowledgement.submitted || current == acknowledgement.displayed
    }

    fun remaining(read: ReviewQueueRead): Int =
        (read.queue.total - debits(read).size).coerceAtLeast(0)

    fun qaCleanRemaining(read: ReviewQueueRead): Int =
        (read.queue.qaCleanPending - debits(read).count { it.qaClean }).coerceAtLeast(0)
}

/** Four fifths of the way through the loaded screenful, which is where the next one is fetched. */
private const val REVIEW_PREFETCH_FRACTION = 0.8

/**
 * Whether to fetch the next screenful now.
 *
 * At four fifths rather than at the end, because a reviewer in a rhythm reaches the end of twenty-
 * five cards faster than a request comes back — and the alternative they met at the end was a card
 * that said "That's this screenful done" with a button on it, which is a queue asking to be
 * re-started rather than a queue.
 *
 * A scope that has filtered the loaded page down to nothing still fetches: there is demonstrably
 * nothing left to read in it, and the next page may hold the work the reviewer asked for.
 */
internal fun shouldPrefetchNextScreenful(
    cursor: Int,
    loaded: Int,
    hasMore: Boolean,
    inFlight: Boolean,
): Boolean {
    if (!hasMore || inFlight) return false
    if (loaded <= 0) return true
    return cursor >= ceil(loaded * REVIEW_PREFETCH_FRACTION).toInt()
}

/**
 * The screenfuls fetched after the first one, and the cursor that reaches the next.
 *
 * Held as one object so the whole of it is replaced when the queue is reloaded: a half-reset paging
 * state is how an appended page outlives the queue it was appended to.
 */
internal class ReviewPaging {
    var appended by mutableStateOf(emptyList<ReviewItemDto>())
        private set
    var more by mutableStateOf<String?>(null)
        private set
    var loading by mutableStateOf(false)
        private set
    var failed by mutableStateOf(false)
        private set
    var latestRead by mutableStateOf<ReviewQueueRead?>(null)
        private set
    var countsFailed by mutableStateOf(false)
        private set

    private var seeded = false

    fun seed(nextCursor: String?) {
        if (seeded) return
        seeded = true
        more = nextCursor
    }

    /**
     * One more screenful, appended.
     *
     * A failed prefetch keeps both the loaded strings and the cursor. The page offers an explicit
     * retry instead of treating a failed next page as the end of the queue.
     */
    suspend fun fetch(slug: String, locale: String, basis: ReviewReadBasis? = null, feedback: ReviewFeedback? = null) {
        if (loading) return
        val cursor = more ?: return
        loading = true
        failed = false
        try {
            val page = ApiClient.reviewQueue(slug, locale, after = cursor)
            appended = appended + page.items
            more = page.nextCursor
            if (basis != null && feedback != null && !feedback.readStayedStable(basis)) {
                // The page's total might already exclude the committed row. Keep its cards and
                // cursor, discard ambiguous global counts, and obtain a stable count/authority
                // read without silently rebinding the claims which are still on screen.
                feedback.requireCurrentRead()
                countsFailed = false
                try {
                    latestRead = feedback.readCurrentCounts(slug, locale, basis.reloadToken)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    countsFailed = true
                }
            } else {
                latestRead = basis?.let { ReviewQueueRead(page, it) }
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            failed = true
        } finally {
            loading = false
        }
    }
}
